import { useRef, useState } from 'react';

import { api } from '@/services';
import { getErrorDetails } from '@/utils';

// ---------------------------------------------------------------------------
// Polling schedule (simple, predictable ramp-down)
//
// Each tier runs for `forMs`, then moves to the next.
// The last tier repeats indefinitely.
// ---------------------------------------------------------------------------
const POLL_TIERS = [
  { forMs: 30_000, everyMs: 5000 }, // 0–30s: initial confirmation
  { forMs: 90_000, everyMs: 15_000 }, // 30s–2min
  { forMs: 3 * 60_000, everyMs: 30_000 }, // 2–5min
  { forMs: 10 * 60_000, everyMs: 60_000 }, // 5–15min
  { forMs: 15 * 60_000, everyMs: 90_000 }, // 15–30min
  { forMs: Infinity, everyMs: 120_000 }, // 30min+
];

const MAX_PROGRESS_RETRIES = 3;

const useConversionPolling = ({ onUnauthorized }) => {
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [conversion, setConversion] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  const timeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const pollStartAtRef = useRef(0);
  const isPollingRef = useRef(false);

  // --- helpers: polling lifecycle ------------------------------------------

  const stopPolling = () => {
    isPollingRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const resetState = () => {
    stopPolling();
    retryCountRef.current = 0;
    setError(null);
    setProgress(0);
    setConversion(null);
    setIsComplete(false);
    setIsConverting(false);
  };

  const getPollInterval = () => {
    const elapsed = Date.now() - pollStartAtRef.current;

    let cursor = 0;

    for (const tier of POLL_TIERS) {
      cursor += tier.forMs;
      if (elapsed < cursor) return tier.everyMs;
    }

    // Fallback (should never happen if last tier is Infinity)
    return POLL_TIERS[POLL_TIERS.length - 1].everyMs;
  };

  // --- helpers: error handling ----------------------------------------------

  const handleProgressTerminalState = (status, payload) => {
    const isSuccess = status === 'success';
    const isFailure = status === 'failure';
    const isIncomplete = status === 'incomplete';

    // Ignore non-terminal states
    if (!isSuccess && !isFailure && !isIncomplete) return;

    stopPolling();
    retryCountRef.current = 0;

    if (isFailure) {
      setError(payload.errors);
    } else {
      setConversion(payload);
      setIsComplete(true);
    }

    // Tiny delay on success to let the user visually see 100%
    setTimeout(() => setIsConverting(false), isSuccess ? 500 : 0);
  };

  const handleProgressError = (err) => {
    const { is401, is429, is500, message } = getErrorDetails(err);

    // 401 → stop immediately and bubble up auth
    if (is401) {
      stopPolling();
      retryCountRef.current = 0;
      setIsConverting(false);
      onUnauthorized();
      return;
    }

    // 429 → skip this tick, let the next scheduled poll retry.
    // Don't count against retry budget -- rate limits are transient
    // and the tiered polling schedule already provides natural backoff.
    if (is429) {
      return;
    }

    // Retry only for transient server errors
    if (is500) {
      retryCountRef.current += 1;

      if (retryCountRef.current > MAX_PROGRESS_RETRIES) {
        stopPolling();
        retryCountRef.current = 0;
        setError(
          'The conversion started but we could not retrieve its progress.'
        );
        // Intentionally do not force isConverting=false here
        // setIsConverting(false);
      }

      // If retries remain, allow the next scheduled tick
      return;
    }

    // Non-retryable error → stop
    stopPolling();
    retryCountRef.current = 0;
    setError(message);
    setIsConverting(false);
  };

  // --- helpers: progress polling --------------------------------------------

  const handleProgressResponse = ({ status, progress, ...rest }) => {
    setProgress(progress);
    retryCountRef.current = 0; // any successful tick resets retries
    handleProgressTerminalState(status, rest);
  };

  const pollOnce = async (conversionId) => {
    try {
      const progressRes = await api.getProgress(conversionId);

      handleProgressResponse(progressRes);
    } catch (err) {
      handleProgressError(err);
    }
  };

  const startPolling = (conversionId) => {
    stopPolling();

    pollStartAtRef.current = Date.now();
    isPollingRef.current = true;
    retryCountRef.current = 0;

    const tick = async () => {
      // Guard: polling may have been stopped by a terminal state
      if (!isPollingRef.current) return;

      await pollOnce(conversionId);

      // pollOnce() may stop polling internally
      if (!isPollingRef.current) return;

      timeoutRef.current = setTimeout(tick, getPollInterval());
    };

    // Poll immediately — don't wait for the first interval
    tick();
  };

  // --- public API -----------------------------------------------------------

  return {
    error,
    setError,
    progress,
    conversion,
    isComplete,
    resetState,
    stopPolling,
    isConverting,
    startPolling,
    setIsComplete,
    setConversion,
    setIsConverting,
  };
};

export default useConversionPolling;
